import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { MeetingPiPOverlay } from './MeetingPiPOverlay';
import { useUserOrganizationUuid } from 'hooks/useCurrentUser';
import { logger } from 'utils/logger';

type IJitsiAuth = {
  app_id: string;
  token: string;
};

export interface MeetingRoomData {
  room_data: {
    title: string;
    id?: number | string;
    internal_id?: string;
    key?: string;
    organization_uuid?: string;
    event_id?: string;
  };
}

type MeetingContextValue = {
  isPiP: boolean;
  isActive: boolean;
  isFullscreen: boolean;
  activeRoom: MeetingRoomData | null;
  startPiP: (room: MeetingRoomData, auth: IJitsiAuth) => void;
  stopPiP: () => void;
  leaveMeeting: () => void;
  registerInPageMeeting: (room: MeetingRoomData, auth: IJitsiAuth, onReadyCb?: () => void) => void;
  unregisterInPageMeeting: () => void;
  toggleFullscreen: () => void;
  attachToElement: (el: HTMLElement) => void;
  detachAttachment: () => void;
};

const MeetingContext = createContext<MeetingContextValue | undefined>(undefined);

const PIP_STORAGE_KEY = 'meeting_pip_active';
const PIP_ROOM_KEY = 'meeting_pip_room';
const PIP_AUTH_KEY = 'meeting_pip_auth';

export const MeetingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const organizationUuid = useUserOrganizationUuid();

  const [isPiP, setIsPiP] = useState<boolean>(false);
  const [room, setRoom] = useState<MeetingRoomData | null>(null);
  const [roomName, setRoomName] = useState<string | null>(null);
  const [auth, setAuth] = useState<IJitsiAuth | null>(null);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [inPageActive, setInPageActive] = useState<boolean>(false);
  const inPageRoomRef = useRef<MeetingRoomData | null>(null);
  const inPageAuthRef = useRef<IJitsiAuth | null>(null);
  const externalApiRef = useRef<any>(null);
  const onReadyRef = useRef<(() => void) | null>(null);

  logger.debug('[MeetingProvider]', {
    isPiP,
    room,
    roomName,
    auth,
    isActive,
    isFullscreen,
    inPageActive,
  });

  // Attachment state (overlay position over in-page placeholder)
  const attachedElementRef = useRef<HTMLElement | null>(null);
  const [attachedRect, setAttachedRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const [isAttached, setIsAttached] = useState<boolean>(false);
  const resizeObsRef = useRef<ResizeObserver | null>(null);
  const [dragPos, setDragPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Restore persisted state on mount
  useEffect(() => {
    try {
      const savedPiP = localStorage.getItem(PIP_STORAGE_KEY) === 'true';
      if (savedPiP) {
        const savedRoom = localStorage.getItem(PIP_ROOM_KEY);
        const savedAuth = localStorage.getItem(PIP_AUTH_KEY);
        if (savedRoom && savedAuth) {
          setRoom(JSON.parse(savedRoom));
          setAuth(JSON.parse(savedAuth));
          setIsPiP(true);
          setIsActive(true);
        }
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  // Persist state changes
  useEffect(() => {
    try {
      localStorage.setItem(PIP_STORAGE_KEY, isPiP ? 'true' : 'false');
      if (isPiP && room && auth) {
        localStorage.setItem(PIP_ROOM_KEY, JSON.stringify(room));
        localStorage.setItem(PIP_AUTH_KEY, JSON.stringify(auth));
      } else {
        localStorage.removeItem(PIP_ROOM_KEY);
        localStorage.removeItem(PIP_AUTH_KEY);
      }
    } catch {
      // Ignore localStorage errors
    }
  }, [isPiP, room, auth]);

  // Build URL-safe room name with embedded context (no slashes)
  useEffect(() => {
    const makeSlug = (value: string) =>
      (value ?? '')
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '');

    const buildRoomName = () => {
      if (!room) return null;
      // ALWAYS include org and room internal id (fallbacks applied if absent)
      const orgRaw =
        organizationUuid ||
        room.room_data.organization_uuid ||
        'unknown';
      const ridRaw = String(room.room_data.internal_id ?? room.room_data.id ?? 'unknown');
      const eidRaw = room.room_data.event_id || '';
      const titleRaw = room.room_data.title ?? '';

      const org = makeSlug(orgRaw);
      const rid = makeSlug(ridRaw);
      const eid = makeSlug(eidRaw);
      const title = makeSlug(titleRaw);

      let encoded = `org-${org}--room-${rid}`;
      if (eid) encoded += `--event-${eid}`;
      if (title) encoded += `--t-${title}`;
      return encoded;
    };

    const nextName = buildRoomName();
    if (nextName) setRoomName(nextName);
  }, [room, organizationUuid]);

  const startPiP = useCallback((r: MeetingRoomData, a: IJitsiAuth) => {
    setRoom(r);
    setAuth(a);
    // Ensure we are not attached to any in-page container when switching to PiP
    setIsAttached(false);
    attachedElementRef.current = null;
    setAttachedRect(null);
    setIsPiP(true);
    setIsActive(true);
  }, []);

  const stopPiP = useCallback(() => {
    setIsPiP(false);
    setIsFullscreen(false);
    setDragPos({ x: 0, y: 0 });
  }, []);

  const leaveMeeting = useCallback(() => {
    try {
      externalApiRef.current?.executeCommand?.('hangup');
    } catch {
      // Ignore hangup errors
    }
    setIsActive(false);
    setIsPiP(false);
    setIsFullscreen(false);
    setRoom(null);
    setAuth(null);
    setInPageActive(false);
    inPageRoomRef.current = null;
    inPageAuthRef.current = null;
    setDragPos({ x: 0, y: 0 });
    setIsAttached(false);
    attachedElementRef.current = null;
    setAttachedRect(null);
    try {
      localStorage.removeItem(PIP_STORAGE_KEY);
      localStorage.removeItem(PIP_ROOM_KEY);
      localStorage.removeItem(PIP_AUTH_KEY);
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => {
      const next = !prev;
      if (next) setDragPos({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const registerInPageMeeting = useCallback(
    (r: MeetingRoomData, a: IJitsiAuth, onReadyCb?: () => void) => {
      logger.debug('[registerInPageMeeting]', { r, a, onReadyCb });
      inPageRoomRef.current = r;
      inPageAuthRef.current = a;
      setRoom(r);
      setAuth(a);
      setIsActive(true); // ensure global Jitsi instance is the one created initially
      setIsPiP(false);
      setInPageActive(true);
      onReadyRef.current = onReadyCb || null;
    },
    []
  );

  const unregisterInPageMeeting = useCallback(() => {
    setInPageActive(false);
    inPageRoomRef.current = null;
    inPageAuthRef.current = null;
  }, []);

  // Detect navigation changes (pushState/replaceState/popstate)
  useEffect(() => {
    const notifyLocationChange = () => {
      const event = new Event('locationchange');
      window.dispatchEvent(event);
    };

    const origPushState = history.pushState;
    const origReplaceState = history.replaceState;
    history.pushState = function (this: History, ...args: Parameters<History['pushState']>) {
      const ret = origPushState.apply(this, args);
      notifyLocationChange();
      return ret;
    } as typeof history.pushState;
    history.replaceState = function (this: History, ...args: Parameters<History['replaceState']>) {
      const ret = origReplaceState.apply(this, args);
      notifyLocationChange();
      return ret;
    } as typeof history.replaceState;

    window.addEventListener('popstate', notifyLocationChange);

    const onLocationChange = () => {
      try {
        const url = new URL(window.location.href);
        const pathname = url.pathname;
        const segments = pathname.split('/').filter(Boolean);
        const lastSegment = segments[segments.length - 1] || '';

        // Compute active id (from current room)
        const activeId = room ? String(room.room_data.internal_id ?? room.room_data.id ?? '') : '';
        const onMeetingPage = !!activeId && lastSegment === activeId;
        const anchorMissing =
          !attachedElementRef.current || !attachedElementRef.current.isConnected;

        // If user is not on meeting page OR anchor disappeared, ensure PiP is enabled
        if (room && auth && (!onMeetingPage || anchorMissing)) {
          if (!isPiP) {
            startPiP(room, auth);
          } else {
            // Already in PiP but anchor might still be set; force detach
            attachedElementRef.current = null;
            setIsAttached(false);
            setAttachedRect(null);
          }
        }

        // If user returned to the same room id, stop PiP so in-page takes over
        if (isPiP && room) {
          const currentId = String(room.room_data.internal_id ?? room.room_data.id ?? '');
          if (currentId && lastSegment === currentId) {
            stopPiP();
          }
        }
      } catch {
        // Ignore URL parsing errors
      }
    };
    window.addEventListener('locationchange', onLocationChange);

    return () => {
      window.removeEventListener('popstate', notifyLocationChange);
      window.removeEventListener('locationchange', onLocationChange);
      history.pushState = origPushState;
      history.replaceState = origReplaceState;
    };
  }, [inPageActive, isPiP, startPiP, room, auth, stopPiP]);

  // Listen for global logout event to leave the meeting immediately
  useEffect(() => {
    const onLogout = () => {
      try {
        leaveMeeting();
      } catch {
        // Ignore logout errors
      }
    };
    // Using CustomEvent name 'logout' dispatched from UserProvider
    window.addEventListener('logout', onLogout as EventListener);
    return () => window.removeEventListener('logout', onLogout as EventListener);
  }, [leaveMeeting]);

  const handleDragPosChange = useCallback((pos: { x: number; y: number }) => {
    setDragPos(pos);
  }, []);

  const value = useMemo(
    () => ({
      isPiP,
      isActive,
      isFullscreen,
      activeRoom: room,
      startPiP,
      stopPiP,
      leaveMeeting,
      registerInPageMeeting,
      unregisterInPageMeeting,
      toggleFullscreen,
      attachToElement: (el: HTMLElement) => {
        try {
          attachedElementRef.current = el;
          setIsAttached(true);
          const computeRect = () => {
            if (!attachedElementRef.current) return;
            const rect = attachedElementRef.current.getBoundingClientRect();
            setAttachedRect({
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
            });
          };
          computeRect();
          // Observe size changes
          if ('ResizeObserver' in window) {
            resizeObsRef.current?.disconnect();
            resizeObsRef.current = new ResizeObserver(computeRect);
            resizeObsRef.current.observe(attachedElementRef.current);
          }
          // Listen to scroll/resize
          const onScrollOrResize = () => computeRect();
          window.addEventListener('scroll', onScrollOrResize, true);
          window.addEventListener('resize', onScrollOrResize);
          // Cleanup when element changes
          return () => {
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
            resizeObsRef.current?.disconnect();
          };
        } catch {
          // Ignore attachment errors
        }
      },
      detachAttachment: () => {
        setIsAttached(false);
        attachedElementRef.current = null;
        setAttachedRect(null);
      },
    }),
    [
      isPiP,
      isActive,
      isFullscreen,
      room,
      startPiP,
      stopPiP,
      leaveMeeting,
      registerInPageMeeting,
      unregisterInPageMeeting,
      toggleFullscreen,
    ]
  );

  return (
    <MeetingContext.Provider value={value}>
      {children}
      <MeetingPiPOverlay
        isActive={isActive}
        isPiP={isPiP}
        isFullscreen={isFullscreen}
        isAttached={isAttached}
        room={room}
        auth={auth}
        roomName={roomName}
        attachedRect={attachedRect}
        dragPos={dragPos}
        onDragPosChange={handleDragPosChange}
        onLeaveMeeting={leaveMeeting}
        onToggleFullscreen={toggleFullscreen}
        externalApiRef={externalApiRef}
        onReadyRef={onReadyRef}
      />
    </MeetingContext.Provider>
  );
};

export const useMeeting = () => {
  const ctx = useContext(MeetingContext);
  if (!ctx) throw new Error('useMeeting must be used within MeetingProvider');
  return ctx;
};
